(() => {
  const WISHLIST_STORAGE_KEY = 'cloudymattressWishlist';

  function formatMoney(cents, symbol) {
    if (cents === null || cents === undefined || isNaN(cents)) return '';
    const amount = cents / 100;
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${symbol}${formatted}`;
  }

  function readWishlist() {
    try {
      const rawWishlist = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
      const parsedWishlist = JSON.parse(rawWishlist || '[]');
      return Array.isArray(parsedWishlist) ? parsedWishlist : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(
        new CustomEvent('cloudymattress:wishlist-updated', {
          detail: { items },
        })
      );
    } catch (error) {
      // localStorage can be unavailable in private or locked-down browsers.
    }
  }

  function syncWishlistButton(button, items) {
    if (!button) return;

    const productId = button.dataset.productId;
    const isActive = items.some(item => String(item.id) === String(productId));
    const buttonLabel = isActive ? button.dataset.labelAdded : button.dataset.labelDefault;
    const labelNode = button.querySelector('[data-wishlist-label]');

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute('aria-label', buttonLabel);

    if (labelNode) {
      labelNode.textContent = buttonLabel;
    }
  }

  function swapMainImage(section, fullSrc, altText) {
    const mainImage = section.querySelector('#mainImage');
    if (!mainImage || !fullSrc) return;
    if (mainImage.dataset.currentSrc === fullSrc) return;

    mainImage.classList.add('is-loading');

    const preloader = new Image();
    preloader.onload = () => {
      mainImage.src = fullSrc;
      mainImage.removeAttribute('srcset');
      mainImage.dataset.currentSrc = fullSrc;
      if (altText) {
        mainImage.alt = altText;
      }
      mainImage.classList.remove('is-loading');
    };
    preloader.onerror = () => {
      mainImage.src = fullSrc;
      mainImage.removeAttribute('srcset');
      mainImage.dataset.currentSrc = fullSrc;
      mainImage.classList.remove('is-loading');
    };
    preloader.src = fullSrc;
  }

  function initThumbs(section) {
    const thumbs = section.querySelectorAll('#thumbs img');
    const mainImage = section.querySelector('#mainImage');
    if (!thumbs.length || !mainImage) return;

    let currentIndex = 0;
    const images = Array.from(thumbs);
    images.forEach((img, index) => {
      if (img.classList.contains('active')) currentIndex = index;
    });

    const baseVisibleCount = parseInt(section.dataset.thumbsVisible || '5', 10);
    const prevBtn = section.querySelector('[data-thumb-prev]');
    const nextBtn = section.querySelector('[data-thumb-next]');
    const imagePrev = section.querySelector('[data-image-prev]');
    const imageNext = section.querySelector('[data-image-next]');

    function getVisibleCount() {
      const isNarrow = window.matchMedia('(max-width: 768px)').matches;
      if (isNarrow) {
        return Math.min(3, images.length);
      }
      return baseVisibleCount;
    }

    function updateThumbVisibility() {
      const visibleCount = getVisibleCount();
      if (!prevBtn || !nextBtn || images.length <= visibleCount) {
        images.forEach(img => { img.style.display = ''; });
        if (prevBtn) prevBtn.disabled = images.length <= 1;
        if (nextBtn) nextBtn.disabled = images.length <= 1;
        return;
      }

      const start = Math.max(0, Math.min(currentIndex - Math.floor(visibleCount / 2), images.length - visibleCount));
      const end = start + visibleCount;
      images.forEach((img, idx) => {
        img.style.display = idx >= start && idx < end ? '' : 'none';
      });
      prevBtn.disabled = images.length <= 1;
      nextBtn.disabled = images.length <= 1;
    }

    function activateIndex(index) {
      const total = images.length;
      const wrapped = ((index % total) + total) % total;
      const img = images[wrapped];
      if (!img) return;
      currentIndex = wrapped;
      swapMainImage(section, img.dataset.full || img.src, img.alt);
      images.forEach(t => t.classList.remove('active'));
      img.classList.add('active');
      updateThumbVisibility();
    }

    thumbs.forEach(img => {
      img.addEventListener('click', () => {
        currentIndex = images.indexOf(img);
        activateIndex(currentIndex);
      });
    });

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener('click', () => {
        activateIndex(currentIndex - 1);
      });

      nextBtn.addEventListener('click', () => {
        activateIndex(currentIndex + 1);
      });
    }

    if (imagePrev && imageNext) {
      imagePrev.addEventListener('click', () => {
        activateIndex(currentIndex - 1);
      });
      imageNext.addEventListener('click', () => {
        activateIndex(currentIndex + 1);
      });
    }
    const swipeArea = mainImage.parentElement;
    if (swipeArea && swipeArea.dataset.swipeBound !== 'true') {
      swipeArea.dataset.swipeBound = 'true';
      let touchStartX = 0;
      let touchStartY = 0;

      swipeArea.addEventListener('touchstart', event => {
        const touch = event.changedTouches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });

      swipeArea.addEventListener('touchend', event => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        activateIndex(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
      }, { passive: true });
    }

    updateThumbVisibility();
    window.addEventListener('resize', updateThumbVisibility);
  }

  function updateTotals(section) {
    const priceCents = parseInt(section.dataset.priceCents, 10);
    const currencySymbol = section.dataset.currencySymbol || '$';
    const qtyInput = section.querySelector('.qty-box input[name="quantity"]');
    const totalEl = section.querySelector('[data-total-price]');

    if (!qtyInput || !totalEl || isNaN(priceCents)) return;

    const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    qtyInput.value = qty;
    totalEl.textContent = formatMoney(priceCents * qty, currencySymbol);
  }

  function initQty(section) {
    const qtyBox = section.querySelector('.qty-box');
    if (!qtyBox) return;

    const qtyInput = qtyBox.querySelector('input[name="quantity"]');
    if (!qtyInput) return;

    qtyBox.querySelectorAll('[data-qty-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.qtyStep, 10);
        const current = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, current + step);
        updateTotals(section);
      });
    });

    qtyInput.addEventListener('input', () => updateTotals(section));
  }

  function renderStockState(stockEl, isAvailable, inventoryQty) {
    if (!stockEl) return;

    if (!isAvailable) {
      stockEl.textContent = 'Out of stock';
      stockEl.classList.add('stock--out');
      return;
    }

    if (!isNaN(inventoryQty) && inventoryQty > 0) {
      stockEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17"> <g id="Group_342" data-name="Group 342" transform="translate(-1204 -569)"> <circle id="Ellipse_42" data-name="Ellipse 42" cx="8.5" cy="8.5" r="8.5" transform="translate(1204 569)" fill="#c5e8c4"/> <rect id="Rectangle_4590" data-name="Rectangle 4590" width="5" height="5" rx="1" transform="translate(1210 575)" fill="#048100"/> </g> </svg> Only ${inventoryQty} left in stock`;
    } else {
      stockEl.textContent = 'In stock';
    }

    stockEl.classList.remove('stock--out');
  }

  function updatePurchaseState(section, state = {}) {
    const addToCartButton = section.querySelector('[data-add-to-cart-button]');
    const addToCartWrapper = section.querySelector('[data-add-to-cart-wrapper]');
    const paymentButtons = section.querySelector('[data-payment-buttons]');
    const stockEl = section.querySelector('.stock');
    const isAvailable = state.available !== false;
    const inventoryQty = Number.isFinite(state.inventoryQty)
      ? state.inventoryQty
      : parseInt(section.dataset.productVariantInventoryQty || '', 10);
    const addToCartLabel = section.dataset.addToCartLabel || 'Add to cart';
    const soldOutLabel = section.dataset.soldOutLabel || 'Sold out';
    const soldOutStyle = section.dataset.soldOutStyle || 'line';

    section.dataset.productVariantAvailable = isAvailable ? 'true' : 'false';

    if (!isNaN(inventoryQty)) {
      section.dataset.productVariantInventoryQty = String(inventoryQty);
    }

    if (addToCartButton) {
      addToCartButton.classList.remove('is-sold-out', 'is-sold-out--line', 'is-sold-out--button');
      addToCartButton.textContent = isAvailable ? addToCartLabel : soldOutLabel;
      addToCartButton.disabled = !isAvailable;

      if (isAvailable) {
        addToCartButton.removeAttribute('aria-disabled');
      } else {
        addToCartButton.setAttribute('aria-disabled', 'true');
        addToCartButton.classList.add('is-sold-out', `is-sold-out--${soldOutStyle}`);
      }
    }

    if (addToCartWrapper) {
      addToCartWrapper.classList.toggle('single-addto-cart--sold-out', !isAvailable);
    }

    if (paymentButtons) {
      paymentButtons.hidden = !isAvailable;
    }

    renderStockState(stockEl, isAvailable, inventoryQty);
  }

  function initProductForm(section) {
    const form = section.querySelector('form.product-form');
    if (!form || form.dataset.productFormInit === 'true') return;

    form.dataset.productFormInit = 'true';

    form.addEventListener('submit', event => {
      if (section.dataset.productVariantAvailable === 'false') {
        event.preventDefault();
      }
    });
  }

  function initVariantButtons(section) {
    const variantInput = section.querySelector('[data-variant-id-input]');
    const priceDisplay = section.querySelector('[data-price-display]');
    const compareDisplay = section.querySelector('[data-compare-display]');
    const saveDisplay = section.querySelector('[data-save-display]');
    const groupedButtons = section.querySelectorAll('[data-option-position][data-option-value]');
    const variantsJsonNode = section.querySelector('[data-product-variants-json]');

    function applyVariantState(variantData) {
      if (!variantData) return;

      const currencySymbol = section.dataset.currencySymbol || '$';
      const variantId = variantData.id;
      const priceCents = parseInt(variantData.price, 10);
      const compareCents = parseInt(variantData.compare_at_price, 10);
      const inventoryQty = parseInt(variantData.inventory_quantity, 10);
      const variantAvailable = variantData.available === true;

      if (variantInput) {
        variantInput.value = variantId;
      }

      section.dataset.productVariantId = variantId;
      section.dataset.productVariantAvailable = variantAvailable ? 'true' : 'false';

      if (!isNaN(priceCents)) {
        section.dataset.priceCents = priceCents;
        if (priceDisplay) {
          priceDisplay.textContent = formatMoney(priceCents, currencySymbol);
        }
        updateTotals(section);
      }

      if (compareDisplay) {
        if (!isNaN(compareCents) && compareCents > priceCents) {
          compareDisplay.textContent = formatMoney(compareCents, currencySymbol);
        } else {
          compareDisplay.textContent = '';
        }
      }

      if (saveDisplay) {
        if (!isNaN(compareCents) && compareCents > priceCents) {
          saveDisplay.textContent = `Save ${formatMoney(compareCents - priceCents, currencySymbol)}`;
        } else {
          saveDisplay.textContent = '';
        }
      }

      updatePurchaseState(section, { available: variantAvailable, inventoryQty });

      if (groupedButtons.length) {
        groupedButtons.forEach(button => {
          const optionPosition = parseInt(button.dataset.optionPosition, 10);
          const optionKey = `option${optionPosition}`;
          const isActive = button.dataset.optionValue === (variantData[optionKey] || '');
          button.classList.toggle('active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      const variantImage = variantData.featured_image;
      const variantTitle = variantData.title || '';
      if (variantImage) {
        const thumbs = section.querySelectorAll('#thumbs img');
        if (thumbs.length) {
          const match = Array.from(thumbs).find(t => t.dataset.full === variantImage);
          if (match) {
            match.click();
          } else {
            swapMainImage(section, variantImage, variantTitle);
          }
        } else {
          swapMainImage(section, variantImage, variantTitle);
        }
      }
    }

    function parseVariants() {
      if (!variantsJsonNode) return [];

      try {
        const parsedVariants = JSON.parse(variantsJsonNode.textContent || '[]');
        return Array.isArray(parsedVariants) ? parsedVariants : [];
      } catch (error) {
        return [];
      }
    }

    function currentVariantFromList(variants) {
      const currentVariantId = section.dataset.productVariantId || '';
      return (
        variants.find(variant => String(variant.id) === String(currentVariantId)) ||
        variants.find(variant => variant.available) ||
        variants[0] ||
        null
      );
    }

    function matchesSelections(variant, selections) {
      return Object.keys(selections).every(optionKey => {
        const selectedValue = selections[optionKey];
        if (!selectedValue) return true;
        return variant[optionKey] === selectedValue;
      });
    }

    function findMatchingVariant(variants, nextSelections, changedOptionKey) {
      const exactAvailable = variants.find(variant => variant.available && matchesSelections(variant, nextSelections));
      if (exactAvailable) return exactAvailable;

      const exactMatch = variants.find(variant => matchesSelections(variant, nextSelections));
      if (exactMatch) return exactMatch;

      const changedValue = nextSelections[changedOptionKey];
      const filteredVariants = variants.filter(variant => variant[changedOptionKey] === changedValue);
      if (!filteredVariants.length) return null;

      let bestVariant = filteredVariants[0];
      let bestScore = -1;

      filteredVariants.forEach(variant => {
        let score = variant.available ? 100 : 0;

        Object.keys(nextSelections).forEach(optionKey => {
          if (optionKey !== changedOptionKey && nextSelections[optionKey] && variant[optionKey] === nextSelections[optionKey]) {
            score += 1;
          }
        });

        if (score > bestScore) {
          bestScore = score;
          bestVariant = variant;
        }
      });

      return bestVariant;
    }

    function updateGroupedButtonStates(variants, activeVariant) {
      if (!groupedButtons.length || !activeVariant) return;

      groupedButtons.forEach(button => {
        const optionPosition = parseInt(button.dataset.optionPosition, 10);
        const optionKey = `option${optionPosition}`;
        const optionValue = button.dataset.optionValue;
        const siblingSelections = {};

        groupedButtons.forEach(otherButton => {
          const otherPosition = parseInt(otherButton.dataset.optionPosition, 10);
          const otherKey = `option${otherPosition}`;
          if (otherKey === optionKey) return;

          siblingSelections[otherKey] = activeVariant[otherKey] || '';
        });

        const hasMatch = variants.some(variant => variant[optionKey] === optionValue && matchesSelections(variant, siblingSelections));
        button.disabled = !hasMatch;
        button.classList.toggle('is-disabled', !hasMatch);
      });
    }

    if (groupedButtons.length && variantsJsonNode) {
      const variants = parseVariants();
      if (!variants.length) return;

      let activeVariant = currentVariantFromList(variants);
      if (!activeVariant) return;

      applyVariantState(activeVariant);
      updateGroupedButtonStates(variants, activeVariant);

      groupedButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.disabled) return;

          const optionPosition = parseInt(button.dataset.optionPosition, 10);
          const optionKey = `option${optionPosition}`;
          const nextSelections = {
            option1: activeVariant.option1 || '',
            option2: activeVariant.option2 || '',
            option3: activeVariant.option3 || '',
          };

          nextSelections[optionKey] = button.dataset.optionValue;

          const matchedVariant = findMatchingVariant(variants, nextSelections, optionKey);
          if (!matchedVariant) return;

          activeVariant = matchedVariant;
          applyVariantState(activeVariant);
          updateGroupedButtonStates(variants, activeVariant);
        });
      });

      return;
    }

    const buttons = section.querySelectorAll('[data-variant-id]');
    if (!buttons.length) return;

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        buttons.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');

        const variantId = btn.dataset.variantId;
        const priceCents = parseInt(btn.dataset.variantPrice, 10);
        const compareCents = parseInt(btn.dataset.variantCompare, 10);
        const inventoryQty = parseInt(btn.dataset.variantQty, 10);
        const variantAvailable = btn.dataset.variantAvailable === 'true';
        const currencySymbol = section.dataset.currencySymbol || '$';

        if (variantInput) {
          variantInput.value = variantId;
        }

        section.dataset.productVariantId = variantId;
        section.dataset.productVariantAvailable = variantAvailable ? 'true' : 'false';

        if (!isNaN(priceCents)) {
          section.dataset.priceCents = priceCents;
          if (priceDisplay) {
            priceDisplay.textContent = formatMoney(priceCents, currencySymbol);
          }
          updateTotals(section);
        }

        if (compareDisplay) {
          if (!isNaN(compareCents) && compareCents > priceCents) {
            compareDisplay.textContent = formatMoney(compareCents, currencySymbol);
          } else {
            compareDisplay.textContent = '';
          }
        }

        if (saveDisplay) {
          if (!isNaN(compareCents) && compareCents > priceCents) {
            saveDisplay.textContent = `Save ${formatMoney(compareCents - priceCents, currencySymbol)}`;
          } else {
            saveDisplay.textContent = '';
          }
        }

        updatePurchaseState(section, { available: variantAvailable, inventoryQty });

        const variantImage = btn.dataset.variantImage;
        const variantTitle = btn.dataset.variantTitle || btn.textContent.trim();
        if (variantImage) {
          const thumbs = section.querySelectorAll('#thumbs img');
          if (thumbs.length) {
            const match = Array.from(thumbs).find(t => t.dataset.full === variantImage);
            if (match) {
              match.click();
            } else {
              swapMainImage(section, variantImage, variantTitle);
            }
          } else {
            swapMainImage(section, variantImage, variantTitle);
          }
        }
      });
    });
  }

  function initWishlist(section) {
    const button = section.querySelector('[data-wishlist-button]');
    if (!button || button.dataset.wishlistInit === 'true') return;
    button.dataset.wishlistInit = 'true';

    button.dataset.productId = section.dataset.productId || '';

    function syncState() {
      syncWishlistButton(button, readWishlist());
    }

    button.addEventListener('click', () => {
      const productId = section.dataset.productId;
      if (!productId) return;

      const wishlistItems = readWishlist();
      const itemIndex = wishlistItems.findIndex(item => String(item.id) === String(productId));

      if (itemIndex >= 0) {
        wishlistItems.splice(itemIndex, 1);
      } else {
        wishlistItems.push({
          id: productId,
          variantId: section.dataset.productVariantId || '',
          handle: section.dataset.productHandle || '',
          title: section.dataset.productTitle || '',
          url: section.dataset.productUrl || '',
          image: section.dataset.productImage || '',
          vendor: section.dataset.productVendor || '',
          priceCents: parseInt(section.dataset.priceCents, 10) || 0,
        });
      }

      writeWishlist(wishlistItems);
      syncWishlistButton(button, wishlistItems);
    });

    syncState();
  }

  function initCountdown(section) {
    const countdownEl = section.querySelector('[data-countdown]');
    if (!countdownEl) return;

    const targetDate = countdownEl.dataset.countdown;
    if (!targetDate) return;

    const daysEl = section.querySelector('#days');
    const hoursEl = section.querySelector('#hours');
    const minutesEl = section.querySelector('#minutes');
    const secondsEl = section.querySelector('#seconds');
    if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

    const targetTime = new Date(targetDate).getTime();
    if (Number.isNaN(targetTime)) return;

    function updateTimer() {
      const now = Date.now();
      const diff = targetTime - now;

      if (diff <= 0) {
        daysEl.textContent = '00';
        hoursEl.textContent = '00';
        minutesEl.textContent = '00';
        secondsEl.textContent = '00';
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      daysEl.textContent = String(days).padStart(2, '0');
      hoursEl.textContent = String(hours).padStart(2, '0');
      minutesEl.textContent = String(minutes).padStart(2, '0');
      secondsEl.textContent = String(seconds).padStart(2, '0');
    }

    updateTimer();
    setInterval(updateTimer, 1000);
  }

  function initOfferCountdown(section) {
    const banner = section.querySelector('[data-offer-countdown]');
    if (!banner) return;

    const endValue = banner.dataset.offerEnd;
    if (!endValue) return;

    const endDate = new Date(endValue);
    if (Number.isNaN(endDate.getTime())) return;

    const daysEl = banner.querySelector('[data-offer-days]');
    const hoursEl = banner.querySelector('[data-offer-hours]');
    const minsEl = banner.querySelector('[data-offer-mins]');
    const secsEl = banner.querySelector('[data-offer-secs]');

    function pad(value) {
      return value.toString().padStart(2, '0');
    }

    function tick() {
      const now = Date.now();
      const diff = endDate.getTime() - now;

      if (diff <= 0) {
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minsEl) minsEl.textContent = '00';
        if (secsEl) secsEl.textContent = '00';
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(mins);
      if (secsEl) secsEl.textContent = pad(secs);
    }

    tick();
    setInterval(tick, 1000);
  }

  function initSizeGuide(section) {
    const toggle = section.querySelector('[data-size-guide-toggle]');
    const modal = section.querySelector('[data-size-guide-modal]');
    if (!toggle || !modal) return;
    if (modal.dataset.sizeGuideInit === 'true') return;
    modal.dataset.sizeGuideInit = 'true';

    const closeButtons = modal.querySelectorAll('[data-size-guide-close]');

    function openModal() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('size-guide-open');
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('size-guide-open');
    }

    toggle.addEventListener('click', event => {
      event.preventDefault();
      openModal();
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  function initVendorBrand(section) {
    const brand = section.querySelector('.product-brand');
    const value = section.querySelector('[data-vendor-brand]');
    const logo = section.querySelector('[data-vendor-logo]');
    if (!brand || !value || !logo) return;
    if (logo.dataset.vendorInit === 'true') return;
    logo.dataset.vendorInit = 'true';

    const primarySrc = logo.getAttribute('src');
    const fallbackSrc = logo.dataset.fallbackSrc;

    function showLogo(src) {
      if (src && logo.getAttribute('src') !== src) {
        logo.setAttribute('src', src);
      }
      brand.classList.add('product-brand--has-logo');
      value.classList.add('product-brand__value--has-logo');
    }

    function showText() {
      brand.classList.remove('product-brand--has-logo');
      value.classList.remove('product-brand__value--has-logo');
      logo.remove();
    }

    function tryLogo(src, onFail) {
      if (!src) {
        onFail();
        return;
      }

      const tester = new Image();
      tester.onload = () => showLogo(src);
      tester.onerror = onFail;
      tester.src = src;
    }

    if (logo.complete) {
      if (logo.naturalWidth > 0) {
        showLogo(primarySrc);
        return;
      }

      if (fallbackSrc && fallbackSrc !== primarySrc) {
        tryLogo(fallbackSrc, showText);
      } else {
        showText();
      }
      return;
    }

    tryLogo(primarySrc, () => {
      if (fallbackSrc && fallbackSrc !== primarySrc) {
        tryLogo(fallbackSrc, showText);
      } else {
        showText();
      }
    });
  }

  function initSection(section) {
    if (!section || section.dataset.productInit === 'true') return;
    section.dataset.productInit = 'true';

    section.dataset.thumbsVisible = section.dataset.thumbsVisible || section.dataset.thumbsVisibleDefault || '';
    updatePurchaseState(section, {
      available: section.dataset.productVariantAvailable === 'true',
      inventoryQty: parseInt(section.dataset.productVariantInventoryQty || '', 10),
    });
    initThumbs(section);
    initProductForm(section);
    initVariantButtons(section);
    initQty(section);
    updateTotals(section);
    initCountdown(section);
    initOfferCountdown(section);
    initSizeGuide(section);
    initVendorBrand(section);
    initWishlist(section);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.single-product-section').forEach(initSection);
  });

  document.addEventListener('shopify:section:load', event => {
    const sectionEl = document.getElementById(`shopify-section-${event.detail.sectionId}`);
    if (sectionEl) {
      const productSection = sectionEl.querySelector('.single-product-section');
      if (productSection) initSection(productSection);
    }
  });
})();
